import { CSSProperties, useMemo, useState } from "react";
import { flushSync } from "react-dom";
import { useNavigate } from "react-router";

import { Minimize2, Plus } from "lucide-react";
import { StatusPill } from "nazare-ui/components";
import { cn } from "nazare-ui/utils";

import { useDocumentTitle } from "../../../../use-document-title";
import { useSecondaryHeader } from "../../../../secondary-header-context";
import { ScrollShadow, TABLE_HEADER_HEIGHT } from "../../../../scroll-shadow";
import { InfiniteScrollSentinel, SkeletonRow } from "../../../../skeleton-row";
import { useInfiniteRows } from "../../../../use-infinite-rows";
import { useScrollEdges } from "../../../../use-scroll-edges";
import { useSimulatedLoad } from "../../../../use-simulated-load";
import styles from "./scheduled.module.css";

type ScheduleStatus = "enabled" | "disabled";

type ScheduledWorkflow = {
  id: string;
  name: string;
  /** EventBridge-style `cron(...)` / `rate(...)` expression, same shape as
   * the reference dashboard's "Scheduled workflows" card. */
  cron: string;
  status: ScheduleStatus;
  /** Hardcoded display string, not a `Date` — Workflow scripts forbid
   * non-deterministic calls like `new Date()`/`Date.now()`. */
  nextRun: string;
};

/**
 * Full mock roster behind the dashboard's "Scheduled workflows" summary
 * card (`../summary-cards.tsx`, `SCHEDULED_WORKFLOWS`) — same six entries,
 * extended with the rest of the schedule board. Workflow names reuse the
 * real names already established in `../summary-cards.tsx` and
 * `../runs-table.tsx` so the app reads as one consistent system; there is
 * no live workflow-orchestration backend in `platform` yet.
 */
const SCHEDULED_WORKFLOWS: ScheduledWorkflow[] = [
  {
    id: "scheduled-agenda-reminder",
    name: "Agenda Reminder",
    cron: "cron(10 7 ? * FRI *)",
    status: "enabled",
    nextRun: "10 Jul, 14:10",
  },
  {
    id: "scheduled-eval-metrics-extraction",
    name: "Eval Metrics Extraction",
    cron: "cron(30 22 * * ? *)",
    status: "disabled",
    nextRun: "in 6h 46 min",
  },
  {
    id: "scheduled-daily-report-alerts",
    name: "Daily Report Alerts",
    cron: "cron(0 8 * * ? *)",
    status: "enabled",
    nextRun: "in 16h 18 min",
  },
  {
    id: "scheduled-weekly-report-alerts",
    name: "Weekly Report Alerts",
    cron: "cron(0 15 ? * FRI *)",
    status: "enabled",
    nextRun: "10 Jul, 22:00",
  },
  {
    id: "scheduled-sentiment-form-chaser",
    name: "Sentiment Form Chaser",
    cron: "cron(0 7 ? * FRI *)",
    status: "enabled",
    nextRun: "10 Jul, 14:00",
  },
  {
    id: "scheduled-weekly-sentiment-summary",
    name: "Weekly Sentiment Summary",
    cron: "cron(0 8 ? * MON *)",
    status: "enabled",
    nextRun: "13 Jul, 08:00",
  },
  {
    id: "scheduled-ingest-gmail-inbox",
    name: "Ingest Gmail — Inbox",
    cron: "rate(15 minutes)",
    status: "enabled",
    nextRun: "in 12 min",
  },
  {
    id: "scheduled-ingest-gmail-manager",
    name: "Ingest Gmail — Manager",
    cron: "cron(0 * * * ? *)",
    status: "enabled",
    nextRun: "in 42 min",
  },
  {
    id: "scheduled-extract-metrics-backfill",
    name: "Extract Metrics Backfill",
    cron: "cron(0 3 * * ? *)",
    status: "disabled",
    nextRun: "in 4h 20 min",
  },
  {
    id: "scheduled-refresh-portfolio-metrics",
    name: "Refresh Portfolio Metrics",
    cron: "cron(0 6 * * ? *)",
    status: "enabled",
    nextRun: "in 7h 12 min",
  },
  {
    id: "scheduled-sync-calendar-events",
    name: "Sync Calendar Events",
    cron: "rate(30 minutes)",
    status: "enabled",
    nextRun: "in 8 min",
  },
  {
    id: "scheduled-enrich-company-profile",
    name: "Enrich Company Profile",
    cron: "cron(0 5 ? * MON,WED,FRI *)",
    status: "enabled",
    nextRun: "8 Jul, 05:00",
  },
  {
    id: "scheduled-score-deal-sentiment",
    name: "Score Deal Sentiment",
    cron: "cron(0 9 * * ? *)",
    status: "enabled",
    nextRun: "in 10h 26 min",
  },
  {
    id: "scheduled-notify-partner-slack-digest",
    name: "Notify Partner Slack Digest",
    cron: "cron(0 17 ? * FRI *)",
    status: "disabled",
    nextRun: "10 Jul, 17:00",
  },
  {
    id: "scheduled-generate-weekly-digest",
    name: "Generate Weekly Digest",
    cron: "cron(0 8 ? * MON *)",
    status: "enabled",
    nextRun: "13 Jul, 08:00",
  },
  {
    id: "scheduled-archive-stale-threads",
    name: "Archive Stale Threads",
    cron: "cron(0 1 * * ? *)",
    status: "enabled",
    nextRun: "in 2h 34 min",
  },
  {
    id: "scheduled-backup-knowledge-base",
    name: "Backup Knowledge Base",
    cron: "cron(0 3 * * ? *)",
    status: "enabled",
    nextRun: "in 4h 34 min",
  },
  {
    id: "scheduled-reindex-search-corpus",
    name: "Reindex Search Corpus",
    cron: "cron(0 4 ? * SUN *)",
    status: "disabled",
    nextRun: "13 Jul, 04:00",
  },
  {
    id: "scheduled-prune-expired-artefacts",
    name: "Prune Expired Artefacts",
    cron: "cron(0 2 ? * SUN *)",
    status: "enabled",
    nextRun: "13 Jul, 02:00",
  },
  {
    id: "scheduled-send-notification-retry-sweep",
    name: "Send Notification Retry Sweep",
    cron: "rate(5 minutes)",
    status: "enabled",
    nextRun: "in 3 min",
  },
];

/**
 * This board manages far more schedules than the 20 hand-authored above —
 * that was just the curated "top of the board" sample. `TOTAL_SCHEDULED_COUNT`
 * is the real, accurate total this page claims (shown in the header), and
 * `getSchedule(index)` below extends the hardcoded `SCHEDULED_WORKFLOWS`
 * array out to that count with deterministic synthetic rows for infinite
 * scroll, rather than pretending the board stops where the hand-written
 * sample happens to stop.
 */
const TOTAL_SCHEDULED_COUNT = 42;

/** Deterministic 32-bit string hash (FNV-1a) + seeded PRNG — same technique
 * as `runs.tsx`'s `generateSyntheticRun`, so the SAME index always produces
 * the SAME synthetic row (no `Math.random()`/`Date.now()`). */
function hashSeed(input: string): number {
  let hash = 0x811c9dc5;
  for (let i = 0; i < input.length; i += 1) {
    hash ^= input.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return hash >>> 0;
}

function mulberry32(seed: number): () => number {
  let a = seed;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function slugify(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

/** Distinct schedule names/cron expressions already present in
 * `SCHEDULED_WORKFLOWS` — the pools synthetic rows are deterministically
 * drawn from, same convention as `runs.tsx`'s `WORKFLOW_FILTER_VALUES`/
 * `SECONDARY_POOL` (reusing names/crons already established elsewhere in
 * this file rather than inventing unrelated ones — a job like "Ingest
 * Gmail — Inbox" plausibly has more than one schedule on the board). */
const NAME_POOL = SCHEDULED_WORKFLOWS.map((schedule) => schedule.name);
const CRON_POOL = Array.from(
  new Set(SCHEDULED_WORKFLOWS.map((schedule) => schedule.cron)),
);

const MONTH_NAMES = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

function pad2(value: number): string {
  return String(value).padStart(2, "0");
}

// Fixed baseline "now" the synthetic `nextRun` offsets count forward from —
// never `new Date()` with no args. Chosen to land in the same near-term
// window (7-13 Jul 2026) as the hardcoded rows' own absolute dates above.
const NEXT_RUN_BASELINE_MS = new Date(2026, 6, 7, 0, 0, 0).getTime();

/** Mirrors the hardcoded rows' own mix of relative ("in 12 min") and
 * absolute ("10 Jul, 14:10") next-run formats, switching format by how far
 * out the offset is. The hardcoded `SCHEDULED_WORKFLOWS` rows above are
 * NOT sorted by "Next run" (relative and absolute entries are interleaved
 * with no consistent ordering), so unlike `runs.tsx`'s "Created" column
 * there's no monotonic-ordering constraint to preserve here — any plausible
 * offset works regardless of index. */
function formatNextRun(offsetMinutes: number): string {
  if (offsetMinutes < 60) {
    return `in ${offsetMinutes} min`;
  }
  if (offsetMinutes < 24 * 60) {
    const hours = Math.floor(offsetMinutes / 60);
    const minutes = offsetMinutes % 60;
    return `in ${hours}h ${minutes} min`;
  }
  const date = new Date(NEXT_RUN_BASELINE_MS + offsetMinutes * 60_000);
  return `${date.getDate()} ${MONTH_NAMES[date.getMonth()]}, ${pad2(date.getHours())}:${pad2(date.getMinutes())}`;
}

/** A schedule beyond the hand-authored `SCHEDULED_WORKFLOWS` board —
 * deterministic per `index`, so scrolling to the same position always shows
 * the same row. */
function generateSyntheticSchedule(index: number): ScheduledWorkflow {
  const rand = mulberry32(hashSeed(`scheduled-${index}`));
  const name = NAME_POOL[Math.floor(rand() * NAME_POOL.length)];
  const cron = CRON_POOL[Math.floor(rand() * CRON_POOL.length)];
  // ~80% enabled / ~20% disabled, matching the hardcoded board's own ratio
  // (16 enabled of 20 rows above).
  const status: ScheduleStatus = rand() < 0.8 ? "enabled" : "disabled";
  const offsetMinutes = 1 + Math.floor(rand() * 10_080);

  return {
    id: `scheduled-${slugify(name)}-${index}`,
    name,
    cron,
    status,
    nextRun: formatNextRun(offsetMinutes),
  };
}

function getSchedule(index: number): ScheduledWorkflow {
  return index < SCHEDULED_WORKFLOWS.length
    ? SCHEDULED_WORKFLOWS[index]
    : generateSyntheticSchedule(index);
}

type StatusFilter = "all" | ScheduleStatus;

const STATUS_FILTERS: { key: StatusFilter; label: string }[] = [
  { key: "all", label: "All" },
  { key: "enabled", label: "Enabled" },
  { key: "disabled", label: "Disabled" },
];

/**
 * Full-page `/workflows/scheduled` route — the "maximised" view of the
 * dashboard's "Scheduled workflows" summary card (`../summary-cards.tsx`).
 * Renders the same schedule data as a proper full-width table (Name, Cron,
 * Status, Next run) instead of the dashboard's compact list-row style. The
 * shell's breadcrumbs already read "Workflows > Scheduled workflows" for
 * this route, so no page-level title is rendered here — just the table
 * card. The Enabled/Disabled status filter lives in the shell's secondary
 * header row rather than inline on the page. All data is static mock
 * content — there is no live workflow-orchestration backend in `platform`
 * yet.
 */
export function WorkflowScheduledPage() {
  useDocumentTitle("Scheduled Workflows");

  const navigate = useNavigate();
  const goToDashboard = () => {
    if (!document.startViewTransition) {
      navigate("/workflows");
      return;
    }
    document.startViewTransition(() => flushSync(() => navigate("/workflows")));
  };

  const isInitialLoading = useSimulatedLoad();
  const { ref: tableWrapRef, atTop, atBottom } = useScrollEdges<HTMLDivElement>();
  const { loadedCount, isLoadingMore, hasMore, sentinelRef } = useInfiniteRows({
    totalCount: TOTAL_SCHEDULED_COUNT,
    initialCount: SCHEDULED_WORKFLOWS.length,
    pageSize: 20,
    scrollRootRef: tableWrapRef,
  });
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");

  // Filtering applies to whatever's been loaded so far, same as any
  // client-side filter over an infinite-scrolled list — it doesn't fetch
  // ahead to find matches beyond `loadedCount`.
  const loadedSchedules = useMemo(
    () => Array.from({ length: loadedCount }, (_, index) => getSchedule(index)),
    [loadedCount],
  );

  const filtered = useMemo(
    () =>
      statusFilter === "all"
        ? loadedSchedules
        : loadedSchedules.filter((schedule) => schedule.status === statusFilter),
    [loadedSchedules, statusFilter],
  );

  const isFiltered = statusFilter !== "all";

  const filterRow = useMemo(
    () => (
      <div className={styles.toolbarRow}>
        <div className={styles.filterBar} role="tablist" aria-label="Filter by status">
          {STATUS_FILTERS.map((filter) => {
            const active = filter.key === statusFilter;
            return (
              <button
                key={filter.key}
                type="button"
                role="tab"
                aria-selected={active}
                className={cn(styles.filterTab, active && styles.filterTabActive)}
                onClick={() => setStatusFilter(filter.key)}
              >
                {filter.label}
              </button>
            );
          })}
        </div>
        {/* Flex spacer, matching the QA Automation toolbar's `.tabBarRow`
            pattern: packs the filter tabs left and reserves the right side
            for a header-level action button. The reference dashboard's
            "Scheduled workflows" card has no such action today (just the
            heading + count badge), so nothing renders here yet — this is
            structural groundwork for if one's added later. */}
        <span className={styles.spacer} />
        <button type="button" className={styles.startRunButton}>
          <Plus size={14} aria-hidden />
          New schedule
        </button>
      </div>
    ),
    [statusFilter],
  );

  useSecondaryHeader(filterRow);

  return (
    <div className={styles.page}>
      <section
        className={styles.card}
        style={{ viewTransitionName: "wf-card-scheduled" } as unknown as CSSProperties}
      >
        <header className={styles.header}>
          <span className={styles.summary}>
            {isFiltered
              ? `${filtered.length} of ${loadedCount} loaded`
              : `${TOTAL_SCHEDULED_COUNT} scheduled workflows`}
          </span>
          <button
            type="button"
            className={styles.minimiseButton}
            onClick={goToDashboard}
            aria-label="Minimise to dashboard"
            title="Minimise to dashboard"
          >
            <Minimize2 className={styles.minimiseIcon} aria-hidden="true" />
          </button>
        </header>

        <div className={styles.scrollShadowWrapper}>
        <div ref={tableWrapRef} className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th className={styles.th} scope="col">
                  Name
                </th>
                <th className={styles.th} scope="col">
                  Cron
                </th>
                <th className={styles.th} scope="col">
                  Status
                </th>
                <th className={`${styles.th} ${styles.thRight}`} scope="col">
                  Next run
                </th>
              </tr>
            </thead>
            <tbody>
              {isInitialLoading
                ? Array.from({ length: 8 }, (_, index) => (
                    <SkeletonRow key={`initial-skeleton-${index}`} columns={4} />
                  ))
                : (
                  <>
                    {filtered.map((schedule) => (
                      <tr key={schedule.id} className={styles.row}>
                        <td className={styles.td}>
                          <span className={styles.name}>{schedule.name}</span>
                        </td>
                        <td className={`${styles.td} ${styles.cron}`}>{schedule.cron}</td>
                        <td className={styles.td}>
                          <StatusPill tone={schedule.status === "enabled" ? "success" : "neutral"}>
                            {schedule.status === "enabled" ? "Enabled" : "Disabled"}
                          </StatusPill>
                        </td>
                        <td className={`${styles.td} ${styles.nextRun}`}>{schedule.nextRun}</td>
                      </tr>
                    ))}
                    {hasMore && <InfiniteScrollSentinel sentinelRef={sentinelRef} columns={4} />}
                    {isLoadingMore &&
                      Array.from({ length: 3 }, (_, index) => (
                        <SkeletonRow key={`skeleton-${index}`} columns={4} />
                      ))}
                  </>
                )}
            </tbody>
          </table>
        </div>
        <ScrollShadow atTop={atTop} atBottom={atBottom} topOffset={TABLE_HEADER_HEIGHT} />
        </div>
      </section>
    </div>
  );
}
